import {
  AnnotationState,
  TaskGroupType,
} from 'packages/lib/api/annotations/contract/enums';
import { useQuery } from 'packages/lib/utils/hooks/use-query';
import { FunctionComponent, useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { z } from 'zod';
import {
  multiTasksAtom,
  multitaskToolAnnotationStateAtom,
  multitaskToolAssigneeAtom,
  multitaskToolSampleGroupAtom,
} from 'packages/at-shell/state/recoil/multitask-workflow';
import { userEmail } from 'packages/lib/auth';
import useSWR from 'swr';
import { useHistory, useLocation } from 'react-router-dom';
import { sampleGroupZod } from 'packages/lib/api/annotations/contract/sample-group';
import { apiGet } from 'packages/lib/api/annotations/rest';
import {
  sampleGroupAtom,
  taskGroupAtom,
  taskQueueAtom,
  toolAliasAtom,
} from 'packages/at-shell/state/recoil/task';
import { dispatchErrorToast } from 'packages/lib/common-ui/toast/api-toast';
import {
  useAssignedTasks,
  usePeekUnassignedTasks,
  useTaskQueue,
} from 'packages/task-man/service/pickup';
import { mapAnnotationStateToRole } from 'packages/task-man/service/state-machine';
import {
  annotationStateParamZod,
  assigneeParamZod,
  objectTokenParamZod,
  sampleGroupParamZod,
} from 'packages/at-shell/utils/multitask-query-param-validators';
import { useTasksByObjectToken } from 'packages/task-man/service/pickup/use-tasks-by-object-token';
import { tasksByObjectTokenAtom } from 'packages/at-shell/state/recoil/multitask-workflow';
import { useDoesToolRequireLinkedTasks } from 'packages/at-shell/utils/use-does-tool-require-linked-tasks';
import { DEFAULT_BATCH_SIZE } from 'packages/task-man/service/pickup/task-queue';
import { devModeAtom } from 'packages/at-shell/state/recoil/ui';
import { toolAliasRegex } from 'packages/task-man/components/sample-group-task-queues';

export const zMultitaskWorkflowQueryParams = z.object({
  sample_group: sampleGroupParamZod,
  annotation_state: annotationStateParamZod,
  assignee: assigneeParamZod,
  dev_mode: z
    .string()
    .optional()
    .transform((prev) => !!prev),
});

export const zLinkedTaskWorkflowQueryParams =
  zMultitaskWorkflowQueryParams.extend({
    object_token: objectTokenParamZod,
  });

export type MultitaskWorkflowQueryParams = z.infer<
  typeof zMultitaskWorkflowQueryParams
>;

export type LinkedTaskWorkflowQueryParams = z.infer<
  typeof zLinkedTaskWorkflowQueryParams
>;

export const useToolMultitaskQueryParams = () => {
  const query = useQuery();
  return Object.fromEntries(query) as unknown as MultitaskWorkflowQueryParams;
};

export const useToolLinkedTaskQueryParams = () => {
  const query = useQuery();
  return Object.fromEntries(query) as unknown as LinkedTaskWorkflowQueryParams;
};

export const zMultitaskTestWorkflowQueryParams = z.object({
  task_tokens_for_test: z.string().optional(),
});

export const useToolMultitaskTestUserQueryParams = () => {
  const query = useQuery();
  const queryEntries = Object.fromEntries(query);
  return zMultitaskTestWorkflowQueryParams.parse(queryEntries);
};

export const SyncMultiTaskWorkflowParams: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const { data: user } = useSWR('user-email', userEmail);
  const { dev_mode, sample_group, assignee, annotation_state } =
    useToolMultitaskQueryParams();

  /**
   * ANCHOR - Tool Alias
   */
  const {
    location: { pathname },
  } = history;
  const toolAlias = pathname.match(toolAliasRegex)?.groups?.tool_alias;
  const setToolAlias = useSetRecoilState(toolAliasAtom);
  useEffect(() => {
    setToolAlias(toolAlias);
  }, [toolAlias, setToolAlias]);

  /**
   * ANCHOR – Developer Mode
   */
  const setDevMode = useSetRecoilState(devModeAtom);
  useEffect(() => {
    setDevMode(dev_mode);
  }, [dev_mode, setDevMode]);

  const { data: sampleGroup, error: sampleGroupError } = useSWR(
    sample_group,
    (token: string) => apiGet(`/sample-groups/${token}`, sampleGroupZod.parse)
  );

  const setSampleGroupToken = useSetRecoilState(multitaskToolSampleGroupAtom);
  const setSampleGroup = useSetRecoilState(sampleGroupAtom);
  useEffect(() => {
    setSampleGroupToken(sample_group);
    if (sampleGroup?.token) {
      setSampleGroup(sampleGroup);
    } else {
      setSampleGroup(undefined);
    }
    if (sampleGroupError) {
      dispatchErrorToast(
        `There was an error when fetching your your sample group: ${sampleGroupError.message}`
      );
    }
  }, [
    sample_group,
    setSampleGroupToken,
    sampleGroup,
    setSampleGroup,
    sampleGroupError,
  ]);

  const setAssignee = useSetRecoilState(multitaskToolAssigneeAtom);
  useEffect(() => {
    if (assignee) {
      setAssignee(assignee);
    } else if (user) {
      setAssignee(user);
      /**
       * Add current user to URL Query Param `assignee`, if the param is missing
       */
      query.append('assignee', user);
      history.replace({
        pathname: location.pathname,
        search: `?${decodeURIComponent(query.toString())}`,
      });
    }
  }, [assignee, history, location.pathname, query, setAssignee, user]);

  const setAnnotationState = useSetRecoilState(
    multitaskToolAnnotationStateAtom
  );
  useEffect(() => {
    setAnnotationState(annotation_state);
  }, [annotation_state, setAnnotationState]);

  return null;
};

const SyncAssignedMultiTaskDataEffect: FunctionComponent<
  React.PropsWithChildren<{
    user: string;
    annotationState: AnnotationState;
    taskGroupType: TaskGroupType;
  }>
> = ({ user, annotationState, taskGroupType }) => {
  const setTaskGroupAtom = useSetRecoilState(taskGroupAtom);
  const { sample_group, object_token } = useToolLinkedTaskQueryParams();
  const isLinkedTaskWorkflow = useDoesToolRequireLinkedTasks();
  const setTaskQueue = useSetRecoilState(taskQueueAtom);
  const setTasks = useSetRecoilState(multiTasksAtom);
  const { data: taskQueue } = useTaskQueue({
    role: mapAnnotationStateToRole(annotationState),
    sampleGroupToken: sample_group,
    userEmail: user,
    annotationState,
    taskGroupType,
    loadTasksByObjectToken: isLinkedTaskWorkflow,
    objectToken: object_token,
  });

  useEffect(() => {
    if (taskQueue) {
      setTaskQueue(taskQueue);
      setTaskGroupAtom(taskQueue.taskGroup);
    }
  }, [taskQueue, setTaskGroupAtom, setTaskQueue]);

  const { assignedTasks } = useAssignedTasks(
    taskQueue,
    isLinkedTaskWorkflow ? 1000 : DEFAULT_BATCH_SIZE
  );
  const { unassignedTasks } = usePeekUnassignedTasks(
    taskQueue,
    isLinkedTaskWorkflow ? 1000 : DEFAULT_BATCH_SIZE
  );
  useEffect(() => {
    if (assignedTasks?.length) {
      setTasks(assignedTasks);
    } else if (unassignedTasks?.length) {
      setTasks(unassignedTasks);
    }
  }, [assignedTasks, unassignedTasks, setTasks]);

  // For tools that use tasks linked by Object Token (i.e. Scene Enrichment), populate tasksByObjectTokenAtom
  const { data: tasksByObjectToken } = useTasksByObjectToken(
    taskQueue,
    object_token
  );
  const setTasksByObjectToken = useSetRecoilState(tasksByObjectTokenAtom);
  useEffect(() => {
    if (tasksByObjectToken) {
      setTasksByObjectToken(tasksByObjectToken);
    }
  }, [tasksByObjectToken, setTasksByObjectToken]);

  return null;
};

// Special version for unit/load tests that does NOT call useAssignedTasks / setTasks,
// as those are set instead by TestAutomationAnnotationTool
const SyncMultiTaskTestDataEffect: FunctionComponent<
  React.PropsWithChildren<{
    user: string;
    annotationState: AnnotationState;
    taskGroupType: TaskGroupType;
  }>
> = ({ user, annotationState, taskGroupType }) => {
  const setTaskGroupAtom = useSetRecoilState(taskGroupAtom);
  const { sample_group, object_token } = useToolLinkedTaskQueryParams();
  const linkTasksByObject = useDoesToolRequireLinkedTasks();
  const setTaskQueue = useSetRecoilState(taskQueueAtom);
  const setTasks = useSetRecoilState(multiTasksAtom);
  const { data: taskQueue } = useTaskQueue({
    role: mapAnnotationStateToRole(annotationState),
    sampleGroupToken: sample_group,
    userEmail: user,
    annotationState,
    taskGroupType,
    loadTasksByObjectToken: linkTasksByObject,
    objectToken: object_token,
  });

  useEffect(() => {
    if (taskQueue) {
      setTaskQueue(taskQueue);
      setTaskGroupAtom(taskQueue.taskGroup);
    }
  }, [setTaskGroupAtom, setTaskQueue, setTasks, taskQueue]);

  return null;
};

export const mapAppPathToTaskGroupType = (path: string): TaskGroupType => {
  if (path.includes('classification-enrichment')) {
    return 'ImageEnrichment';
  }
  if (path.includes('scene-enrichment')) {
    return 'SceneEnrichment';
  }
  throw new Error('Invalid app path.');
};

export const SyncMultiTaskWorkflowData: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const path = useLocation().pathname;
  const { assignee, annotation_state } = useToolMultitaskQueryParams();
  const { task_tokens_for_test } = useToolMultitaskTestUserQueryParams();

  if (!annotation_state) return null;
  if (!assignee) return null;

  // Handle special case that's only for test automation purposes
  const hasTestTokens =
    task_tokens_for_test && task_tokens_for_test.length > 20;

  return hasTestTokens ? (
    <SyncMultiTaskTestDataEffect
      user={assignee}
      annotationState={annotation_state}
      taskGroupType={mapAppPathToTaskGroupType(path)}
    />
  ) : (
    <SyncAssignedMultiTaskDataEffect
      user={assignee}
      annotationState={annotation_state}
      taskGroupType={mapAppPathToTaskGroupType(path)}
    />
  );
};
